import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'horizontal' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex gap-3 p-3 rounded-lg hover:bg-background-100 transition-colors"
      >
        <div className="w-20 h-14 shrink-0 rounded-md overflow-hidden bg-background-200">
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-foreground-800 line-clamp-2 group-hover:text-primary-500 transition-colors">
            {article.title}
          </h4>
          <div className="flex items-center gap-2 mt-1 text-xs text-foreground-400">
            <span>{article.publishDate}</span>
            <span>{article.readCount.toLocaleString()} 阅读</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'horizontal') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex flex-col sm:flex-row gap-4 p-4 rounded-lg hover:bg-background-100 transition-colors"
      >
        <div className="w-full sm:w-48 h-36 shrink-0 rounded-lg overflow-hidden bg-background-200">
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        </div>
        <div className="flex-1 min-w-0 flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              {article.isExclusive && (
                <span className="text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded-full font-medium whitespace-nowrap">
                  独家
                </span>
              )}
              {article.isHot && (
                <span className="text-xs bg-accent-100 text-accent-700 px-2 py-0.5 rounded-full font-medium whitespace-nowrap">
                  热门
                </span>
              )}
              <span className="text-xs text-foreground-400">{article.categoryName}</span>
            </div>
            <h3 className="text-base font-bold text-foreground-900 line-clamp-2 group-hover:text-primary-500 transition-colors">
              {article.title}
            </h3>
            <p className="text-sm text-foreground-500 mt-2 line-clamp-2">
              {article.excerpt}
            </p>
          </div>
          <div className="flex items-center gap-3 mt-3 text-xs text-foreground-400">
            <span>{article.publishDate}</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i> {article.readCount.toLocaleString()}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-chat-3-line"></i> {article.commentCount.toLocaleString()}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.id}`}
      className="group block rounded-lg overflow-hidden hover:bg-background-100 transition-colors"
      data-product-shop=""
    >
      <div className="aspect-[16/10] overflow-hidden bg-background-200">
        <img
          src={article.coverImage}
          alt={article.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
      </div>
      <div className="p-4">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          {article.isExclusive && (
            <span className="text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded-full font-medium whitespace-nowrap">
              独家
            </span>
          )}
          {article.isHot && (
            <span className="text-xs bg-accent-100 text-accent-700 px-2 py-0.5 rounded-full font-medium whitespace-nowrap">
              热门
            </span>
          )}
          <span className="text-xs text-foreground-400">{article.categoryName}</span>
        </div>
        <h3 className="text-base font-bold text-foreground-900 line-clamp-2 group-hover:text-primary-500 transition-colors mb-2">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-500 line-clamp-2 mb-3">
          {article.excerpt}
        </p>
        <div className="flex items-center gap-3 text-xs text-foreground-400">
          <span>{article.publishDate}</span>
          <span className="flex items-center gap-1">
            <i className="ri-eye-line"></i> {article.readCount.toLocaleString()}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-chat-3-line"></i> {article.commentCount.toLocaleString()}
          </span>
        </div>
      </div>
    </Link>
  );
}